import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Pagination from '@mui/material/Pagination';
import Skeleton from '@mui/material/Skeleton';
import { alpha, useTheme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { TOrder } from '@onesource/schemas';
import {
  getMemberOrderByNoQueryFn,
  getMemberOrderByNoQueryKey,
  useGetMemberPaymentPlanByIdQuery,
} from '@onesource/services';
import { useQueries } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { FC, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { HiArrowLeft, HiArrowRight } from 'react-icons/hi2';
import { CONFIG } from 'src/config-global';

import { PageContainer } from '#/components/page-container';

// ----------------------------------------------------------------------

const centsToUSD = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const statusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
  switch (status.toLowerCase()) {
    case 'active': {
      return 'success';
    }
    case 'cancelled':
    case 'expired': {
      return 'error';
    }
    case 'pending': {
      return 'warning';
    }
    default: {
      return 'default';
    }
  }
};

const orderStatusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
  switch (status.toLowerCase()) {
    case 'completed':
    case 'active': {
      return 'success';
    }
    case 'failed':
    case 'cancelled':
    case 'expired': {
      return 'error';
    }
    case 'pending': {
      return 'warning';
    }
    default: {
      return 'default';
    }
  }
};

const formatDate = (iso: string) =>
  new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long', day: 'numeric' }).format(new Date(iso));

const ORDERS_PER_PAGE = 10;

// ----------------------------------------------------------------------

export const PaymentPlanDetailPage: FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { paymentPlanId } = useParams({ from: '/payment-plans/$paymentPlanId' });
  const [ordersPage, setOrdersPage] = useState(1);

  const { data: plan } = useGetMemberPaymentPlanByIdQuery(paymentPlanId);

  const pagedOrderIds = plan?.orderIds.slice((ordersPage - 1) * ORDERS_PER_PAGE, ordersPage * ORDERS_PER_PAGE) ?? [];

  const orderQueries = useQueries({
    queries: pagedOrderIds.map(id => ({
      queryKey: getMemberOrderByNoQueryKey(id),
      queryFn: () => getMemberOrderByNoQueryFn(id),
      enabled: !!plan,
    })),
  });

  useEffect(() => {
    if (!plan) {
      navigate({ to: '/billing', search: { tab: 'payment-plans' } });
    }
  }, [plan, navigate]);

  if (!plan) {
    return null;
  }

  const totalOrderPages = Math.ceil((plan.orderIds.length ?? 0) / ORDERS_PER_PAGE);
  const ordersLoading = orderQueries.some(q => q.isLoading);
  const orders = orderQueries.map(q => q.data).filter(Boolean) as TOrder[];

  return (
    <>
      <Helmet>
        <title>{`Payment Plan #${plan.paymentPlanId} - ${CONFIG.appName}`}</title>
      </Helmet>

      <PageContainer>
        <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              mb: 4,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Link to="/billing" search={{ tab: 'payment-plans' }} style={{ textDecoration: 'none' }}>
                <Button
                  variant="contained"
                  color="inherit"
                  sx={{
                    minWidth: 0,
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    p: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'primary.lighter',
                    color: 'primary.main',
                    boxShadow: 'none',
                    transition: 'background-color 0.2s, color 0.2s',
                    '&:hover': { bgcolor: 'primary.dark', color: '#fff', boxShadow: 'none' },
                  }}
                >
                  <HiArrowLeft size={22} />
                </Button>
              </Link>
              <Typography variant="h4" fontWeight={700}>
                Payment Plan #{plan.paymentPlanId}
              </Typography>
            </Box>
            <Chip
              label={plan.status.charAt(0).toUpperCase() + plan.status.slice(1)}
              color={statusColor(plan.status)}
              sx={{ fontWeight: 600, fontSize: 13, px: 1 }}
            />
          </Box>

          {/* Info cards */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
            {[
              { label: 'Next Payment', value: plan.nextPaymentOn },
              { label: 'Subtotal', value: centsToUSD(plan.subtotal) },
              { label: 'Discount', value: centsToUSD(plan.discount) },
              { label: 'Total', value: centsToUSD(plan.total) },
            ].map(({ label, value }) => (
              <Box
                key={label}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  px: 2.5,
                  py: 2,
                  minWidth: 160,
                  bgcolor: alpha(theme.palette.grey[500], 0.04),
                }}
              >
                <Typography variant="caption" color="text.disabled" fontWeight={500} display="block" mb={0.5}>
                  {label}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Products table */}
          <TableContainer
            sx={{
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              mb: 3,
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.grey[500], 0.04) }}>
                  <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    Qty
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Unit Price
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Total
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plan.products.map(product => (
                  <TableRow key={product.productId} sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {product.productName}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2">{product.quantity}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{centsToUSD(product.price)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {centsToUSD(product.price * product.quantity)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Totals summary */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 5 }}>
            <Box
              sx={{
                width: { xs: '100%', sm: 320 },
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Subtotal
                </Typography>
                <Typography variant="body2">{centsToUSD(plan.subtotal)}</Typography>
              </Box>
              {plan.discount > 0 && (
                <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Discount
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    −{centsToUSD(plan.discount)}
                  </Typography>
                </Box>
              )}
              <Divider />
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  bgcolor: alpha(theme.palette.grey[500], 0.04),
                }}
              >
                <Typography variant="subtitle2" fontWeight={700}>
                  Total
                </Typography>
                <Typography variant="subtitle2" fontWeight={700}>
                  {centsToUSD(plan.total)}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Related Orders */}
          {plan.orderIds.length > 0 && (
            <Box>
              <Typography variant="h6" fontWeight={700} mb={2}>
                Orders
              </Typography>
              <TableContainer
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  mb: 2,
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha(theme.palette.grey[500], 0.04) }}>
                      <TableCell sx={{ fontWeight: 700 }}>Order #</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">
                        Total
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ordersLoading
                      ? pagedOrderIds.map(id => (
                          <TableRow key={id}>
                            <TableCell>
                              <Skeleton variant="text" width={72} />
                            </TableCell>
                            <TableCell>
                              <Skeleton variant="text" width={120} />
                            </TableCell>
                            <TableCell>
                              <Skeleton variant="rounded" width={80} height={24} />
                            </TableCell>
                            <TableCell align="right">
                              <Skeleton variant="text" width={56} />
                            </TableCell>
                            <TableCell align="right">
                              <Skeleton variant="rounded" width={64} height={28} />
                            </TableCell>
                          </TableRow>
                        ))
                      : orders.map(order => (
                          <TableRow key={order.orderNo} sx={{ '&:last-child td': { border: 0 } }}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                #{order.orderNo}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{formatDate(order.date)}</Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                                color={orderStatusColor(order.status)}
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight={600}>
                                {centsToUSD(order.total)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Link
                                to="/orders/$orderNo"
                                params={{ orderNo: String(order.orderNo) }}
                                style={{ textDecoration: 'none' }}
                              >
                                <Button
                                  variant="outlined"
                                  size="small"
                                  color="error"
                                  endIcon={<HiArrowRight size={16} />}
                                  sx={{
                                    minWidth: 88,
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    '& .MuiButton-endIcon': { marginRight: 0 },
                                  }}
                                >
                                  View
                                </Button>
                              </Link>
                            </TableCell>
                          </TableRow>
                        ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {totalOrderPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Pagination
                    count={totalOrderPages}
                    page={ordersPage}
                    onChange={(_, page) => setOrdersPage(page)}
                    variant="outlined"
                    color="primary"
                    shape="rounded"
                    size="large"
                  />
                </Box>
              )}
            </Box>
          )}
        </Container>
      </PageContainer>
    </>
  );
};
